// Ours: how the copied grid sticks, fills and resets its columns, kept out of
// `data-grid-table.tsx` so that file stays close to upstream. All are ours except ReUI's
// `hasDataGridFillColumn`, which moved here with the fill column it now follows.
import type { Column } from '@tanstack/react-table'

import type { DataGridFeatures, DataGridLayoutProps, DataGridTableInstance } from './data-grid'

/**
 * Ours: whether a column takes the pinned styles. It follows where the column sticks, so a column
 * the user can't re-pin, such as row selection, still sticks where the grid pins it.
 */
export function getDataGridCanStick<TData extends object>(
  column: Column<DataGridFeatures, TData, unknown>,
  props: DataGridLayoutProps<NoInfer<TData>>,
): boolean {
  return (
    !!props.tableLayout?.columnsPinnable &&
    (column.getCanPin() || !!getDataGridStickySide(column, props.stickySides))
  )
}

/**
 * Ours: the visible column that takes the free width: the grid's `fillColumnId`, else a
 * `meta.fillWidth` column.
 */
export function getDataGridFillColumnId<TData extends object>(
  table: DataGridTableInstance<TData>,
  fillColumnId?: string,
) {
  const columns = table.getVisibleLeafColumns()
  return (
    columns.find(column => column.id === fillColumnId)?.id ??
    columns.find(column => column.columnDef.meta?.fillWidth)?.id
  )
}

/**
 * Ours: the side whose separator line `column` draws, when it's the inner edge of its sticky run.
 * On a phone the run can end before TanStack's last pinned column, so `getIsLastColumn('start')`
 * would put the line on a column that scrolls.
 */
export function getDataGridStickyEdge<TData extends object>(
  table: DataGridTableInstance<TData>,
  column: Column<DataGridFeatures, TData, unknown>,
  stickySides?: Record<string, 'end' | 'start'>,
): 'end' | 'start' | undefined {
  const side = getDataGridStickySide(column, stickySides)
  if (!side) return undefined
  const pinned =
    side === 'start' ? table.getStartVisibleLeafColumns() : table.getEndVisibleLeafColumns()
  const run = pinned.filter(candidate => getDataGridStickySide(candidate, stickySides) === side)
  const edge = side === 'start' ? run.at(-1) : run[0]
  return edge?.id === column.id ? side : undefined
}

/**
 * Ours: the side a column sticks to. `stickySides` (our sticky policy) leaves out pinned columns
 * that scroll on a narrow screen; without it, every pinned column sticks.
 */
export function getDataGridStickySide<TData extends object>(
  column: Column<DataGridFeatures, TData, unknown>,
  stickySides?: Record<string, 'end' | 'start'>,
): 'end' | 'start' | false {
  return stickySides ? (stickySides[column.id] ?? false) : column.getIsPinned()
}

// A meta.fillWidth column absorbs the free space itself, so the fill cells
// must collapse or the strip would be counted twice.
// Ours: they collapse while the grid's `fillColumnId` is hidden too, so the table spreads the free
// width across its columns rather than leaving a blank strip.
export function hasDataGridFillColumn<TData extends object>(
  table: DataGridTableInstance<TData>,
  fillColumnId?: string,
) {
  return fillColumnId !== undefined || getDataGridFillColumnId(table) !== undefined
}

/**
 * Ours: resets a column's width inside a resize session, as a drag commits one, so whatever
 * saves widths when a resize ends saves the reset too.
 */
export function resetDataGridColumnSize<TData extends object>(
  column: Column<DataGridFeatures, TData, unknown>,
  table: DataGridTableInstance<TData>,
) {
  table.setColumnResizing(old => ({ ...old, isResizingColumn: column.id }))
  column.resetSize()
  table.setColumnResizing(old => ({ ...old, isResizingColumn: false }))
}
